import moment from 'moment';
import { defaultItemRenderer } from './form_utils';

export const EDIT_MODE = 'editMode';
export const ADD_MODE = 'addMode';

const getResponsiveValue = (platform, input, defaultValue) => {
  const items = input || defaultValue;
  if (items && Array.isArray(items) && items.length > 0) {
    if (platform === 'web') {
      return items[0];
    }
    return items.length >= 2 ? items[1] : null;
  }
  return items || defaultValue;
};

class AutoComputePolicy {
  constructor(mode, computeFn) {
    this.mode = mode;
    this.computeFn = computeFn;
  }

  getValue(mode) {
    if (this.mode == ADD_MODE && mode != ADD_MODE) {
      return;
    }
    return this.computeFn();
  }
}

export const BlankStringAutoComputePolicy = new AutoComputePolicy(
  ADD_MODE,
  () => {
    return '';
  }
);

export const CreatedDateAutoComputePolicy = new AutoComputePolicy(
  ADD_MODE,
  () => {
    return moment().toISOString();
  }
);

export const ModifiedDateAutoComputePolicy = new AutoComputePolicy(
  EDIT_MODE,
  () => {
    return moment().toISOString();
  }
);

export class Relation {
  constructor(foreignKey, foreignLabel, queryName, query, options = {}) {
    this.foreignKey = foreignKey;
    this.foreignLabel = foreignLabel;
    this.query = query;
    this.queryName = queryName;
    this.dropdownWidth = options?.dropdownWidth;
  }
}

/**
 * OptionalValues that can be provided are:
 *
 * relation: Relation
 * flex: Int - for flexbox positioning; e.g. 1
 * showInList: Boolean - should this field be shown in listing page?
 * autoComputePolicy: AutoComputePolicy - how to compute auto-values
 */
export class Field {
  constructor(name, title, options = {}) {
    this.name = name;
    this.title = title;
    this.relation = options?.relation;
    this.flex = options.flex || 1;
    this.showInList = options.showInList || false;
    this.nativeTitle = options.nativeTitle || false;
    this.default = options?.default;
    this.autoComputePolicy = options.autoComputePolicy;
    this.quickAdd = options?.quickAdd || false;
    this.columnDef = options.columnDef || [];
  }

  getCell(data) {
    if (data == undefined || data == null) {
      return null;
    }
    const value = data && data[this.name];
    return this?.relation == null
      ? value
      : value
      ? value[this.relation.foreignLabel]
      : null;
  }

  getAutoValue(mode) {
    return null;
  }

  getRelationStyle(platform) {
    return {
      width: getResponsiveValue(platform, this?.relation?.dropdownWidth, [
        '35%',
        '100%',
      ]),
    };
  }

  needsEditorProps() {
    return this.columnDef?.editable && this.relation?.query;
  }

  async getRefData(client, variables) {
    try {
      const queryName = this.relation.queryName;
      const key = this.relation.foreignKey;
      const label = this.relation.foreignLabel;
      console.log('variables', variables);
      const records = await client.query({
        query: this.relation.query,
        variables,
      });
      return records.data[queryName].reduce((map, it) => {
        map[it[key]] = it[label];
        return map;
      }, {});
    } catch (error) {
      console.log(
        `Exception when fetching reference data for query: ${this.relation.queryName}`,
        error
      );
    }
    return [];
  }

  async getEditorProps(client, variables) {
    const queryName = this.relation.queryName;
    const key = this.relation.foreignKey;
    const label = this.relation.foreignLabel;
    const cellEditorParams = this.columnDef?.cellEditorParams || {};

    const refData = await this.getRefData(client, variables);
    console.log(refData);

    const valueFormatter = (params) => refData[params.value];

    return {
      valueFormatter,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        ...cellEditorParams,
        values: Object.keys(refData),
        valueFormatter: (params) => refData[params.newValue],
      },

      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.keys(refData),
        valueFormatter,
      },
    };
  }

  async getColumnDef(client, variables) {
    let editorProps = this.needsEditorProps()
      ? await this.getEditorProps(client, variables)
      : {};

    const def = {
      headerName: this.title,
      field: this.name,
      flex: this.flex,
      resizable: true,
      ...this.columnDef,
      ...editorProps,
    };

    return def;
  }
}

export class ModelMetadata {
  constructor(name, options = {}) {
    this.name = name;
    this.icon = options?.icon || 'lemon';
    this.pluralName = options?.plural || `${name}s`;
    this.primaryKey = options?.primaryKey || 'id';
    this.fetchQuery = options?.fetchQuery;
    this.getItemQuery = options?.getItemQuery;
    this.refetchQueries = options?.refetchQueries || [`All${this.pluralName}`];
    this.addMutation = options?.addMutation;
    this.updateMutation = options?.updateMutation;
    this.deleteMutation = options?.deleteMutation;
    this.fields = options?.fields || [];
    this.updateCache = options?.updateCache || (() => {});
    this.validationSchema = options?.validationSchema;
    this.createModelForm = options?.createModelForm;
    this.createItemView = options?.createItemView;
    this.createItemDetailView = options?.createItemDetailView;
    this.createListItemRenderer =
      options?.createListItemRenderer || defaultItemRenderer;
    this.nativeListField = options?.nativeListField || 'name';
  }

  get editScreen() {
    return 'EditScreen';
  }

  get addScreen() {
    return 'AddScreen';
  }

  get viewScreen() {
    return 'ItemViewScreen';
  }

  get queryAll() {
    return `query${this.name}`;
  }

  getListFieldNames() {
    return this.getListFields().map((f) => f.name);
  }

  getListFields() {
    return this.fields.filter((f) => f.showInList);
  }

  getNativeListField() {
    const field = this.fields.filter((f) => f.nativeTitle);
    return field[0] || this.fields[0];
  }

  getPrimaryKeyField() {
    const field = this.fields.filter((f) => f.name === this.primaryKey);
    return Array.isArray(field) ? field[0] : { name: 'id' };
  }

  find(name) {
    return this.fields.find((f) => f.name == name);
  }

  hasField(name) {
    return this.find(name) != -1;
  }

  async getColumnDefs(client, variables = {}) {
    return Promise.all(
      this.getListFields().map(async (f) => {
        const fv = (variables && variables[f.name]) || {};
        return f.getColumnDef(client, fv);
      })
    );
  }

  /*
    The input data might consist of nested objects in the case of relations.
    This method is used to flatten the object and remove the nested objects.
  */
  getFlattenedValues(data, mode = EDIT_MODE) {
    let result = {};

    if (this.primaryKey in data) {
      result[this.primaryKey] = data[this.primaryKey];
    }

    for (const key in data) {
      const field = this.find(key);
      if (field === undefined) {
        continue;
      }
      // console.log(`processing ${key} - got field: ${field?.name}`)

      const value = data[key];
      if (value != null && field?.relation != null) {
        result[key] = value[field.relation.foreignKey];
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  getShapedValues(data, mode) {
    let result = {};
    for (const key in data) {
      const field = this.find(key);

      if (field === undefined || field.name == this.primaryKey) {
        continue;
      }

      const value = data[key];
      if (value != null && field?.relation != null) {
        const obj = {};
        obj[field.relation.foreignKey] = value;
        result[key] = obj;
      } else {
        result[key] = value;
      }
    }

    // add auto-generated values (like created_date, modified_date, etc.)
    this.fields.forEach((field) => {
      if (field.default || field.autoComputePolicy !== undefined) {
        const autoValue =
          field.default || field?.autoComputePolicy?.getValue(mode);
        if (autoValue != null && !result[field.name]) {
          result[field.name] = autoValue;
        }
      }
    });

    return result;
  }

  mustShowField(fieldName, options = {}) {
    const field = this.find(fieldName);
    return field && options?.quickAdd ? field.quickAdd : true;
  }

  isFieldRequired(fieldName) {
    return (
      this.validationSchema
        .describe()
        ?.fields[fieldName]?.tests.findIndex(({ name }) => name === 'required') >= 0
    );
  }
}
